import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchAuctionTeamSetup,
  fetchAuctionTournamentPlayers,
  fetchTournamentById,
  fetchTournaments,
  saveAuctionTeamSetup,
} from '../../../services/tournamentService';
import { getAuctionManagerPath } from '../../../utils/organization';
import {
  getAuctionManagerPath as getCommunityAuctionManagerPath,
  getCommunityId,
} from '../../../utils/community';
import { getAuctionManagerPath as getClubAuctionManagerPath, getClubId } from '../../../utils/club';

const createLocalId = (prefix) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createTeam = (index, playerCount = 11, teamId = createLocalId('team')) => ({
  id: teamId,
  name: `Team ${index + 1}`,
  logo: '',
  logoUrl: '',
  captain: '',
  wicketKeeper: '',
  viceCaptain: '',
  playerCount,
  players: [],
});

const getPlayerId = (player) => (typeof player === 'string' ? player : player?.id || player?._id || '');
const getTeamLogo = (team) => team?.logo || team?.logoUrl || '';
const getTeamInitial = (team, index) => (team?.name?.trim?.()?.charAt(0) || `T${index + 1}`).charAt(0).toUpperCase();

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const AuctionTeamSetupPage = () => {
  const { token } = useAuth();
  const {
    auctionManagerId,
    organizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    tournamentId,
  } = useParams();
  const [searchParams] = useSearchParams();
  const [playersPerTeam, setPlayersPerTeam] = useState(11);
  const [numberOfTeams, setNumberOfTeams] = useState(2);
  const [teams, setTeams] = useState(() => Array.from({ length: 2 }, (_, index) => createTeam(index, 11)));
  const [playerInputs, setPlayerInputs] = useState(() => Array.from({ length: 2 }, () => []));
  const [playerSearchTerms, setPlayerSearchTerms] = useState(() => Array.from({ length: 2 }, () => ''));
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [tournamentPlayers, setTournamentPlayers] = useState([]);
  const [isLoadingTournaments, setIsLoadingTournaments] = useState(false);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [isLoadingSetup, setIsLoadingSetup] = useState(false);
  const [isSavingSetup, setIsSavingSetup] = useState(false);
  const [setupMessage, setSetupMessage] = useState('');
  const [setupError, setSetupError] = useState('');
  const [tournamentError, setTournamentError] = useState('');
  const [playerLoadError, setPlayerLoadError] = useState('');
  const [activeTeamIndex, setActiveTeamIndex] = useState(0);
  const [logoEditor, setLogoEditor] = useState({
    open: false,
    teamIndex: null,
    imageSrc: '',
    zoom: 1,
    offsetX: 0,
    offsetY: 0,
  });
  const [logoError, setLogoError] = useState('');
  const navigate = useNavigate();
  const communityId = useMemo(() => getCommunityId(routeCommunityId), [routeCommunityId]);
  const clubId = useMemo(() => getClubId(routeClubId), [routeClubId]);
  const isIndividualRoute = Boolean(tournamentId && !organizationId && !communityId && !clubId);

  const selectedTournament = useMemo(
    () => tournaments.find((tournament) => tournament.id === selectedTournamentId),
    [selectedTournamentId, tournaments],
  );

  const totalPlayers = useMemo(
    () => teams.reduce((sum, team) => sum + team.players.length, 0),
    [teams],
  );

  const mappedPlayers = useMemo(
    () =>
      tournamentPlayers.map((player) => ({
        id: player.id || player._id || '',
        name: player.name || player.fullName || 'Unnamed player',
        role: player.role || player.primaryRole || '',
        email: player.email || player.user?.email || '',
        image: player.image || player.profilePhoto || player.media?.profilePhoto || '',
      })),
    [tournamentPlayers],
  );

  const playerLookup = useMemo(() => new Map(mappedPlayers.map((player) => [player.id, player])), [mappedPlayers]);

  const playerAssignments = useMemo(() => {
    const assignments = new Map();

    teams.forEach((team, teamIndex) => {
      (team.players || []).forEach((player) => {
        const playerId = typeof player === 'string' ? player : player.id;
        if (playerId) {
          assignments.set(playerId, teamIndex);
        }
      });
    });

    return assignments;
  }, [teams]);

  useEffect(() => {
    const urlTournamentId = searchParams.get('tournamentId');
    if (urlTournamentId) {
      setSelectedTournamentId(urlTournamentId);
      return;
    }

    if (tournamentId) {
      setSelectedTournamentId(tournamentId);
    }
  }, [searchParams, tournamentId]);

  useEffect(() => {
    if (!token) {
      setTournamentError('Sign in as a tournament or organization admin to load available tournaments.');
      return;
    }

    const loadTournaments = async () => {
      setIsLoadingTournaments(true);
      setTournamentError('');

      try {
        if (isIndividualRoute) {
          const response = await fetchTournamentById(tournamentId, token);
          const individualTournament = response.tournament || response;
          const availableTournaments = [{
            id: individualTournament.id || individualTournament._id || tournamentId,
            name: individualTournament.name || 'Untitled tournament',
          }].filter((item) => Boolean(item.id));

          setTournaments(availableTournaments);
          setSelectedTournamentId((currentTournamentId) => currentTournamentId || tournamentId);
          return;
        }

        const response = await fetchTournaments(token, organizationId, { communityId, clubId });
        const availableTournaments = (response.tournaments || [])
          .map((tournament) => ({
            id: tournament.id || tournament._id || '',
            name: tournament.name || 'Untitled tournament',
          }))
          .filter((tournament) => Boolean(tournament.id));

        setTournaments(availableTournaments);

        if (availableTournaments.length > 0) {
          const defaultTournament = availableTournaments[0];
          setSelectedTournamentId((currentTournamentId) => currentTournamentId || defaultTournament.id);
        }
      } catch (error) {
        setTournamentError(error.message || 'Unable to load tournaments right now.');
      } finally {
        setIsLoadingTournaments(false);
      }
    };

    loadTournaments();
  }, [clubId, communityId, isIndividualRoute, organizationId, token, tournamentId]);

  useEffect(() => {
    if (!selectedTournamentId || !token) return;

    const loadTournamentPlayers = async () => {
      setIsLoadingPlayers(true);
      setPlayerLoadError('');

      try {
        const response = await fetchAuctionTournamentPlayers(selectedTournamentId, token);
        const players = response.players || [];
        if (players.length === 0) {
          const tournamentResponse = await fetchTournamentById(
            selectedTournamentId,
            token,
            organizationId,
            { communityId, clubId }
          );
          setTournamentPlayers(tournamentResponse.tournament?.players || []);
          return;
        }

        setTournamentPlayers(players);
      } catch (error) {
        setTournamentPlayers([]);
        setPlayerLoadError(error.message || 'Unable to load tournament players right now.');
      } finally {
        setIsLoadingPlayers(false);
      }
    };

    loadTournamentPlayers();
  }, [clubId, communityId, organizationId, selectedTournamentId, token]);

  const getDraftKey = (tournamentId) => `auctionTeamSetupDraft_${tournamentId || 'default'}`;

  const loadDraftFromStorage = (tournamentId) => {
    try {
      const draftString = localStorage.getItem(getDraftKey(tournamentId));
      if (!draftString) return null;

      const draft = JSON.parse(draftString);
      if (!draft || typeof draft !== 'object') return null;

      return draft;
    } catch (error) {
      console.error('Unable to load draft', error);
      return null;
    }
  };

  const saveDraftToStorage = (tournamentId, draftPayload) => {
    try {
      const payloadWithTimestamp = { ...draftPayload, savedAt: new Date().toISOString() };
      localStorage.setItem(getDraftKey(tournamentId), JSON.stringify(payloadWithTimestamp));
    } catch (error) {
      console.error('Unable to save draft', error);
    }
  };

  const hydrateTeams = (setup, fallbackPlayersPerTeam = playersPerTeam) => {
    const targetPlayersPerTeam = Number(setup.playersPerTeam) || fallbackPlayersPerTeam;
    const incomingTeams = (setup.teams || []).map((team, index) => ({
      id: team.id || team._id || createLocalId('team'),
      name: team.name || `Team ${index + 1}`,
      logo: team.logo || team.logoUrl || '',
      logoUrl: team.logo || team.logoUrl || '',
      captain: getPlayerId(team.captain),
      wicketKeeper: getPlayerId(team.wicketKeeper),
      viceCaptain: getPlayerId(team.viceCaptain),
      playerCount: Number.isFinite(Number(team.playerCount)) ? Number(team.playerCount) : targetPlayersPerTeam,
      players: team.players || [],
    }));

    const teamCount = incomingTeams.length || 2;
    setNumberOfTeams(teamCount);
    setTeams(
      incomingTeams.length
        ? incomingTeams
        : Array.from({ length: teamCount }, (_, idx) => createTeam(idx, targetPlayersPerTeam)),
    );
    setPlayerInputs(Array.from({ length: teamCount }, () => []));
    setPlayerSearchTerms(Array.from({ length: teamCount }, () => ''));
  };

  useEffect(() => {
    if (!selectedTournamentId || !token) return;

    const resetTeamsForTournament = () => {
      setNumberOfTeams(2);
      setTeams(Array.from({ length: 2 }, (_, idx) => createTeam(idx, playersPerTeam)));
      setPlayerInputs(Array.from({ length: 2 }, () => []));
      setPlayerSearchTerms(Array.from({ length: 2 }, () => ''));
    };

    const loadAuctionSetup = async () => {
      setIsLoadingSetup(true);
      setSetupError('');
      setSetupMessage('');

      const localDraft = loadDraftFromStorage(selectedTournamentId);

      try {
        const response = await fetchAuctionTeamSetup(selectedTournamentId, token);
        const setup = response.setup;

        if (setup) {
          const playersPerTeamValue = setup.playersPerTeam || 11;
          const teamCount = (setup.teams || []).length || 2;
          setPlayersPerTeam(playersPerTeamValue);
          hydrateTeams(setup, playersPerTeamValue);
          setNumberOfTeams(teamCount);
          setSetupMessage(
            setup.status === 'draft'
              ? 'Draft restored from the server. Publish when ready.'
              : 'Saved team setup loaded from the server.'
          );

          saveDraftToStorage(selectedTournamentId, { ...setup, numberOfTeams: teamCount });
          return;
        }

        if (localDraft) {
          const draftPlayersPerTeam = localDraft.playersPerTeam || playersPerTeam;
          setPlayersPerTeam(draftPlayersPerTeam);
          hydrateTeams(localDraft, draftPlayersPerTeam);
          setSetupMessage('Draft restored from this browser. Save or publish when ready.');
          return;
        }

        resetTeamsForTournament();
      } catch (error) {
        setSetupError(error.message || 'Unable to load saved team setup.');
        if (localDraft) {
          const draftPlayersPerTeam = localDraft.playersPerTeam || playersPerTeam;
          setPlayersPerTeam(draftPlayersPerTeam);
          hydrateTeams(localDraft, draftPlayersPerTeam);
          setSetupMessage('Draft restored from this browser. Save or publish when ready.');
        } else {
          resetTeamsForTournament();
        }
      } finally {
        setIsLoadingSetup(false);
      }
    };

    loadAuctionSetup();
  }, [selectedTournamentId, token]);

  const handleTeamCountChange = (event) => {
    const value = Number(event.target.value);
    const safeValue = Number.isNaN(value) ? 1 : Math.max(1, Math.min(12, value));

    setNumberOfTeams(safeValue);
    setTeams((previousTeams) => {
      if (safeValue > previousTeams.length) {
        const teamsToAdd = Array.from({ length: safeValue - previousTeams.length }, (_, addIndex) =>
          createTeam(previousTeams.length + addIndex, playersPerTeam),
        );
        const expandedTeams = previousTeams
          .map((team) => ({ ...team, playerCount: playersPerTeam }))
          .concat(teamsToAdd);
        setActiveTeamIndex((current) => Math.min(current, expandedTeams.length - 1));
        return expandedTeams;
      }

      const trimmedTeams = previousTeams.slice(0, safeValue).map((team) => ({ ...team, playerCount: playersPerTeam }));
      setActiveTeamIndex((current) => Math.min(current, trimmedTeams.length - 1));
      return trimmedTeams;
    });
    setPlayerInputs((previousInputs) => {
      if (safeValue > previousInputs.length) {
        return [...previousInputs, ...Array.from({ length: safeValue - previousInputs.length }, () => [])];
      }

      return previousInputs.slice(0, safeValue);
    });
    setPlayerSearchTerms((previousSearches) => {
      if (safeValue > previousSearches.length) {
        return [...previousSearches, ...Array.from({ length: safeValue - previousSearches.length }, () => '')];
      }

      return previousSearches.slice(0, safeValue);
    });
  };

  const updateTeamField = (index, field, value) => {
    setTeams((previousTeams) =>
      previousTeams.map((team, teamIndex) => (teamIndex === index ? { ...team, [field]: value } : team)),
    );
  };

  const handlePlayersPerTeamChange = (event) => {
    const value = Number(event.target.value);
    const safeValue = Number.isNaN(value) ? playersPerTeam : Math.max(1, Math.min(30, value));
    setPlayersPerTeam(safeValue);
    setTeams((previousTeams) =>
      previousTeams.map((team) => {
        const trimmedPlayers = (team.players || []).slice(0, safeValue);
        const trimmedIds = new Set(trimmedPlayers.map(getPlayerId));

        return {
          ...team,
          playerCount: safeValue,
          players: trimmedPlayers,
          captain: trimmedIds.has(team.captain) ? team.captain : '',
          viceCaptain: trimmedIds.has(team.viceCaptain) ? team.viceCaptain : '',
          wicketKeeper: trimmedIds.has(team.wicketKeeper) ? team.wicketKeeper : '',
        };
      }),
    );
    setSetupMessage('Player counts updated. Any extra players beyond the limit were trimmed.');
  };

  const handleAddPlayer = (index) => {
    const availablePlayerIds = new Set(
      mappedPlayers.filter((player) => !playerAssignments.has(player.id)).map((player) => player.id),
    );
    const selectedPlayerIds = (playerInputs[index] || []).filter((playerId) => availablePlayerIds.has(playerId));
    const selectedPlayers = mappedPlayers.filter((player) => selectedPlayerIds.includes(player.id));
    const currentCount = teams[index]?.players?.length || 0;
    const remainingSlots = Math.max(playersPerTeam - currentCount, 0);

    if (!selectedPlayers.length) {
      setPlayerLoadError('Select at least one player from the tournament roster to add.');
      return;
    }

    if (remainingSlots <= 0) {
      setPlayerLoadError(`Team ${index + 1} already has ${playersPerTeam} players.`);
      return;
    }

    setPlayerLoadError('');
    const playersToAdd = selectedPlayers.slice(0, remainingSlots);
    const blockedSelection = selectedPlayers.length > remainingSlots;

    setTeams((previousTeams) =>
      previousTeams.map((team, teamIndex) => {
        if (teamIndex !== index) return team;

        const existingIds = new Set(
          team.players.map((player) => (typeof player === 'string' ? player : player.id)),
        );

        const newPlayers = playersToAdd.filter((player) => !existingIds.has(player.id));

        if (!newPlayers.length) {
          setPlayerLoadError('All selected players are already listed for this team.');
          return team;
        }

        return { ...team, players: [...team.players, ...newPlayers] };
      }),
    );

    setPlayerInputs((previousInputs) =>
      previousInputs.map((input, inputIndex) => (inputIndex === index ? [] : input)),
    );

    if (blockedSelection) {
      setSetupMessage(`Only the first ${remainingSlots} player(s) were added to Team ${index + 1} to respect the limit.`);
    }
  };

  const handleRemovePlayer = (teamIndex, playerIndex) => {
    setTeams((previousTeams) =>
      previousTeams.map((team, currentIndex) => {
        if (currentIndex !== teamIndex) return team;

        const updatedPlayers = team.players.filter((_, idx) => idx !== playerIndex);
        const playerIds = new Set(updatedPlayers.map(getPlayerId));

        return {
          ...team,
          players: updatedPlayers,
          captain: playerIds.has(team.captain) ? team.captain : '',
          viceCaptain: playerIds.has(team.viceCaptain) ? team.viceCaptain : '',
          wicketKeeper: playerIds.has(team.wicketKeeper) ? team.wicketKeeper : '',
        };
      }),
    );
  };

  const handleRemoveTeam = (teamIndex) => {
    setTeams((previousTeams) => {
      const updated = previousTeams.filter((_, idx) => idx !== teamIndex);
      const nextTeams = updated.length ? updated : [createTeam(0, playersPerTeam)];
      setNumberOfTeams(nextTeams.length);
      setActiveTeamIndex((current) => {
        const fallback = Math.max(nextTeams.length - 1, 0);

        if (current === teamIndex) return Math.min(teamIndex, fallback);
        if (current > teamIndex) return Math.min(current - 1, fallback);
        return Math.min(current, fallback);
      });
      return nextTeams;
    });

    setPlayerInputs((previousInputs) => {
      const updated = previousInputs.filter((_, idx) => idx !== teamIndex);
      return updated.length ? updated : [[]];
    });

    setPlayerSearchTerms((previousSearches) => {
      const updated = previousSearches.filter((_, idx) => idx !== teamIndex);
      return updated.length ? updated : [''];
    });
  };

  const filteredPlayersForTeam = (teamIndex) => {
    const searchTerm = (playerSearchTerms[teamIndex] || '').trim().toLowerCase();

    return mappedPlayers.filter((player) => {
      if (playerAssignments.has(player.id)) {
        return false;
      }

      if (!searchTerm) return true;

      const haystack = `${player.name || ''} ${player.email || ''} ${player.role || ''}`.toLowerCase();
      return haystack.includes(searchTerm);
    });
  };

  const togglePlayerSelection = (teamIndex, playerId, isChecked) => {
    setPlayerInputs((previousInputs) =>
      previousInputs.map((input, inputIndex) => {
        if (inputIndex !== teamIndex) return input;

        if (isChecked) {
          const teamPlayerCount = teams[teamIndex]?.players?.length || 0;
          const pendingCount = input.length;
          const totalIfAdded = teamPlayerCount + pendingCount + 1;

          if (totalIfAdded > playersPerTeam) {
            setPlayerLoadError(`You can only add ${playersPerTeam} players to a team.`);
            return input;
          }

          return Array.from(new Set([...input, playerId]));
        }

        return input.filter((id) => id !== playerId);
      }),
    );
  };

  const persistSetup = async (contextLabel = 'Team updates', status = 'published') => {
    if (!selectedTournamentId) {
      setSetupError('Select a tournament to save your setup.');
      return;
    }

    setSetupError('');
    setSetupMessage('');
    const payload = {
      status,
      playersPerTeam,
      teams: teams.map((team, index) => ({
        id: team.id || team._id,
        name: team.name?.trim?.() || `Team ${index + 1}`,
        logo: getTeamLogo(team),
        logoUrl: getTeamLogo(team),
        captain: getPlayerId(team.captain),
        wicketKeeper: getPlayerId(team.wicketKeeper),
        viceCaptain: getPlayerId(team.viceCaptain),
        playerCount: playersPerTeam,
        players: (team.players || []).map(getPlayerId),
      })),
    };

    try {
      const response = await saveAuctionTeamSetup(selectedTournamentId, payload, token);
      const savedSetup = response.setup;
      if (savedSetup) {
        setPlayersPerTeam(savedSetup.playersPerTeam || playersPerTeam);
        const hydratedTeams = (savedSetup.teams || []).map((team, index) => ({
          id: team.id || team._id || createLocalId('team'),
          name: team.name || `Team ${index + 1}`,
          logo: team.logo || team.logoUrl || '',
          logoUrl: team.logo || team.logoUrl || '',
          captain: getPlayerId(team.captain),
          wicketKeeper: getPlayerId(team.wicketKeeper),
          viceCaptain: getPlayerId(team.viceCaptain),
          playerCount: savedSetup.playersPerTeam || playersPerTeam,
          players: team.players || [],
        }));
        setTeams(hydratedTeams);
        const updatedTeamCount = hydratedTeams.length || numberOfTeams;
        setNumberOfTeams(updatedTeamCount);
        saveDraftToStorage(selectedTournamentId, {
          ...savedSetup,
          numberOfTeams: updatedTeamCount,
        });
      } else {
        saveDraftToStorage(selectedTournamentId, { ...payload, numberOfTeams });
      }
      setSetupMessage(
        status === 'draft'
          ? `${contextLabel} saved as a draft for ${selectedTournament?.name || 'this tournament'}.`
          : `${contextLabel} saved for ${selectedTournament?.name || 'this tournament'}.`
      );
    } catch (error) {
      if (status === 'draft') {
        saveDraftToStorage(selectedTournamentId, { ...payload, numberOfTeams });
        setSetupMessage('Draft saved locally. Unable to sync with the server right now.');
      }
      setSetupError(error.message || 'Unable to save team setup.');
    }
  };

  const handleSaveAll = async () => {
    setIsSavingSetup(true);
    await persistSetup('All teams', 'published');
    setIsSavingSetup(false);
  };

  const handleSaveDraft = async () => {
    if (!selectedTournamentId) {
      setSetupError('Select a tournament to save your setup.');
      return;
    }

    setIsSavingSetup(true);
    await persistSetup('Team updates', 'draft');
    setIsSavingSetup(false);
  };

  const isBusy = isSavingSetup;

  useEffect(() => {
    if (activeTeamIndex > teams.length - 1) {
      setActiveTeamIndex(Math.max(teams.length - 1, 0));
    }
  }, [activeTeamIndex, teams.length]);

  const handleLogoFileChange = (index, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setLogoError('Please choose an image file for the team logo.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoError('');
      setLogoEditor({
        open: true,
        teamIndex: index,
        imageSrc: event.target.result,
        zoom: 1,
        offsetX: 0,
        offsetY: 0,
      });
    };
    reader.readAsDataURL(file);
  };

  const resetLogoEditor = () =>
    setLogoEditor({ open: false, teamIndex: null, imageSrc: '', zoom: 1, offsetX: 0, offsetY: 0 });

  const generateCroppedLogo = async () =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const outputSize = 400;
        canvas.width = outputSize;
        canvas.height = outputSize;
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('Unable to process the image.'));
          return;
        }

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, outputSize, outputSize);

        const scale = logoEditor.zoom;
        const scaledWidth = image.width * scale;
        const scaledHeight = image.height * scale;
        const drawX = (outputSize - scaledWidth) / 2 + logoEditor.offsetX;
        const drawY = (outputSize - scaledHeight) / 2 + logoEditor.offsetY;

        context.drawImage(image, drawX, drawY, scaledWidth, scaledHeight);
        const optimized = canvas.toDataURL('image/jpeg', 0.82);
        resolve(optimized);
      };
      image.onerror = () => reject(new Error('Unable to load the selected image.'));
      image.src = logoEditor.imageSrc;
    });

  const handleApplyLogo = async () => {
    try {
      const cropped = await generateCroppedLogo();
      if (logoEditor.teamIndex !== null) {
        updateTeamField(logoEditor.teamIndex, 'logo', cropped);
        updateTeamField(logoEditor.teamIndex, 'logoUrl', cropped);
      }
    } catch (error) {
      setLogoError(error.message || 'Unable to process logo right now.');
    }
    resetLogoEditor();
  };

  const activeTeam = teams[activeTeamIndex] || teams[0];
  const activeTeamPlayers = activeTeam?.players || [];
  const remainingSlots = Math.max(playersPerTeam - (activeTeamPlayers.length || 0), 0);
  const availablePlayers = filteredPlayersForTeam(activeTeamIndex);
  const teamPlayerOptions = activeTeamPlayers
    .map((player) => {
      const playerId = getPlayerId(player);
      const playerInfo = typeof player === 'string' ? playerLookup.get(playerId) : player;

      return {
        id: playerId,
        label: playerInfo?.name || playerInfo?.email || 'Unnamed player',
      };
    })
    .filter((option) => option.id)
    .sort((first, second) => first.label.localeCompare(second.label));
  const pendingPlayers = playerInputs[activeTeamIndex] || [];
  const activeTeamLabel = activeTeam ? activeTeam.name || `Team ${activeTeamIndex + 1}` : 'Team';
  const auctionManagementPath =
    (isIndividualRoute
      ? `/individual-tournament/${tournamentId}/auction`
      : communityId
        ? getCommunityAuctionManagerPath(communityId, auctionManagerId, tournamentId)
        : clubId
          ? getClubAuctionManagerPath(clubId, auctionManagerId, tournamentId)
          : getAuctionManagerPath(organizationId, auctionManagerId, tournamentId)) || '/management/auction';
  const teamsOverviewPath = useMemo(
    () =>
      `${auctionManagementPath}/teams/overview${
        selectedTournamentId ? `?tournamentId=${selectedTournamentId}` : ''
      }`,
    [auctionManagementPath, selectedTournamentId],
  );

  return (
    <div className="py-4 d-grid gap-4 auction-team-setup-page">
      <div className="card border-0 shadow-sm overflow-hidden">
        <div className="card-body d-flex flex-column flex-lg-row justify-content-between align-items-start gap-3">
          <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <p className="eyebrow text-uppercase text-primary fw-semibold mb-0">Auction manager tools</p>
              <span className="badge bg-success-subtle text-success-emphasis">Interactive</span>
            </div>
            <h1 className="h4 mb-1">Manage auction-selected teams</h1>
            <p className="text-muted mb-0">
              Configure every team manually when the auction manager finalizes rosters without live bidding. Capture core
              leadership roles, roster sizes, and player lists for each squad.
            </p>
          </div>
          <div className="d-flex flex-column flex-lg-row gap-2 align-self-stretch">
            <Link to={auctionManagementPath} className="btn btn-outline-primary w-100">
              Back to auction modules
            </Link>
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              onClick={() => navigate(teamsOverviewPath)}
              disabled={!selectedTournamentId}
            >
              View saved teams
            </button>
            <button
              type="button"
              className="btn btn-outline-dark w-100"
              onClick={handleSaveDraft}
              disabled={isBusy}
            >
              Save as draft
            </button>
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={handleSaveAll}
              disabled={isBusy}
            >
              {isSavingSetup ? 'Saving...' : 'Save all teams'}
            </button>
          </div>
        </div>
      </div>

      {(setupMessage || setupError) && (
        <div className={`alert ${setupMessage ? 'alert-success' : 'alert-danger'} mb-0`} role="alert">
          <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <div className="fw-semibold mb-0">{setupMessage || setupError}</div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => {
                setSetupMessage('');
                setSetupError('');
              }}
            />
          </div>
        </div>
      )}

      <div className="card shadow-sm border-0">
        <div className="card-body d-grid gap-4">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-lg-3">
              <label htmlFor="team-count" className="form-label fw-semibold">
                Number of teams
              </label>
              <input
                id="team-count"
                type="number"
                min="1"
                max="12"
                className="form-control"
                value={numberOfTeams}
                onChange={handleTeamCountChange}
                disabled={isLoadingSetup}
              />
              <small className="text-muted">Choose how many squads you want to manage (1-12).</small>
            </div>
            <div className="col-12 col-lg-4">
              <label htmlFor="tournament-select" className="form-label fw-semibold">
                Source tournament
              </label>
              <select
                id="tournament-select"
                className="form-select"
                disabled={isLoadingTournaments || !token}
                value={selectedTournamentId}
                onChange={(event) => setSelectedTournamentId(event.target.value)}
              >
                <option value="" disabled>
                  {token ? 'Select a tournament to load players' : 'Sign in to choose a tournament'}
                </option>
                {tournaments.map((tournament) => (
                  <option key={tournament.id} value={tournament.id}>
                    {tournament.name}
                  </option>
                ))}
              </select>
              <small className="text-muted">Players will be loaded from this tournament roster.</small>
              {tournamentError && <p className="text-danger small mb-0 mt-1">{tournamentError}</p>}
            </div>
            <div className="col-12 col-lg-5">
              <label htmlFor="players-per-team" className="form-label fw-semibold">
                Players per team (global)
              </label>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <input
                  id="players-per-team"
                  type="number"
                  min="1"
                  max="30"
                  className="form-control"
                  value={playersPerTeam}
                  onChange={handlePlayersPerTeamChange}
                  style={{ maxWidth: '200px' }}
                />
                <div className="small text-muted">
                  Applied to every roster and saved with this tournament setup.
                </div>
              </div>
              <div className="border rounded p-2 bg-light mt-2">
                <div className="fw-semibold">Roster summary</div>
                <p className="mb-0 text-muted">
                  {numberOfTeams} teams | {totalPlayers} players listed | Global size {playersPerTeam} | Average per team{' '}
                  {numberOfTeams ? Math.round(totalPlayers / numberOfTeams) : 0}
                </p>
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-12 d-grid gap-3">
              {isLoadingSetup && <p className="text-muted mb-0">Loading saved team setup...</p>}

              <div className="d-flex flex-column gap-2">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <p className="fw-semibold mb-1">Teams</p>
                    <p className="text-muted mb-0">Switch between squads using compact tabs to reduce scrolling.</p>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <span className="badge bg-secondary-subtle text-secondary-emphasis">{numberOfTeams} team(s)</span>
                    <span className="badge bg-info-subtle text-info-emphasis">{totalPlayers} players tracked</span>
                  </div>
                </div>
                <div
                  className="nav nav-pills gap-2 overflow-auto flex-wrap"
                  style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
                >
                  {teams.map((team, index) => (
                    <button
                      key={`team-${team.id || team._id}`}
                      type="button"
                      className={`btn btn-sm d-flex flex-column align-items-start gap-1 shadow-sm ${
                        activeTeamIndex === index ? 'btn-primary text-white' : 'btn-outline-primary'
                      }`}
                      onClick={() => setActiveTeamIndex(index)}
                      style={{ minWidth: '180px' }}
                    >
                      <span className="fw-semibold text-truncate w-100 d-flex align-items-center gap-2">
                        {getTeamLogo(team) ? (
                          <img
                            src={getTeamLogo(team)}
                            alt={`${team.name || `Team ${index + 1}`} logo`}
                            className="rounded-circle border bg-white"
                            style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                          />
                        ) : (
                          <span
                            className="rounded-circle bg-light text-dark border d-inline-flex align-items-center justify-content-center fw-semibold"
                            style={{ width: '24px', height: '24px', fontSize: '0.75rem' }}
                            aria-hidden="true"
                          >
                            {getTeamInitial(team, index)}
                          </span>
                        )}
                        <span className="text-truncate">{team.name || `Team ${index + 1}`}</span>
                      </span>
                      <span className="small d-flex gap-2 align-items-center">
                        <span className="badge bg-light text-dark border">{team.players.length} listed</span>
                        <span className="badge bg-dark-subtle text-dark">Target {playersPerTeam}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {activeTeam ? (
                <div className="card border-0 shadow-sm">
                  <div className="card-body d-grid gap-3">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-primary-subtle text-primary-emphasis">Team {activeTeamIndex + 1}</span>
                        {getTeamLogo(activeTeam) ? (
                          <img
                            src={getTeamLogo(activeTeam)}
                            alt={`${activeTeamLabel} logo`}
                            className="rounded-circle border"
                            style={{ width: '28px', height: '28px', objectFit: 'cover' }}
                          />
                        ) : (
                          <span
                            className="rounded-circle bg-secondary-subtle text-secondary-emphasis border d-inline-flex align-items-center justify-content-center fw-semibold"
                            style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}
                            aria-hidden="true"
                          >
                            {getTeamInitial(activeTeam, activeTeamIndex)}
                          </span>
                        )}
                        <h2 className="h5 mb-0">{activeTeamLabel}</h2>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="badge bg-info-subtle text-info-emphasis">
                          {activeTeamPlayers.length} players
                        </span>
                        <span className="badge bg-secondary-subtle text-secondary-emphasis">Target {playersPerTeam}</span>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleRemoveTeam(activeTeamIndex)}
                          disabled={teams.length === 1 || isBusy}
                        >
                          Remove team
                        </button>
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label htmlFor={`team-name-${activeTeamIndex}`} className="form-label fw-semibold">
                          Team name
                        </label>
                        <input
                          id={`team-name-${activeTeamIndex}`}
                          type="text"
                          className="form-control"
                          value={activeTeam.name}
                          onChange={(event) => updateTeamField(activeTeamIndex, 'name', event.target.value)}
                          placeholder="e.g., City Strikers"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label htmlFor={`team-logo-url-${activeTeamIndex}`} className="form-label fw-semibold">
                          Team Logo URL
                        </label>
                        <div className="d-grid gap-2">
                          <input
                            id={`team-logo-url-${activeTeamIndex}`}
                            type="text"
                            className="form-control"
                            value={activeTeam.logo || ''}
                            onChange={(event) => {
                              updateTeamField(activeTeamIndex, 'logo', event.target.value);
                              updateTeamField(activeTeamIndex, 'logoUrl', event.target.value);
                            }}
                            placeholder="public image URL"
                          />
                          <label htmlFor={`team-logo-${activeTeamIndex}`} className="form-label fw-semibold small mb-0">
                            Upload team logo image
                          </label>
                          <input
                            id={`team-logo-${activeTeamIndex}`}
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={(event) => handleLogoFileChange(activeTeamIndex, event.target.files?.[0])}
                          />
                          {logoError && <p className="text-danger small mb-0">{logoError}</p>}
                          {getTeamLogo(activeTeam) && (
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={getTeamLogo(activeTeam)}
                                alt={`${activeTeamLabel} logo`}
                                className="rounded border"
                                style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => {
                                  updateTeamField(activeTeamIndex, 'logo', '');
                                  updateTeamField(activeTeamIndex, 'logoUrl', '');
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                          <small className="text-muted">
                            Team Logo URL or upload an image file and crop it before saving.
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="border rounded p-3 d-grid gap-3 bg-light">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div>
                          <p className="fw-semibold mb-1">Add players</p>
                          <p className="text-muted mb-0">Build the team list and keep track of roster depth.</p>
                        </div>
                        <span className="badge bg-info-subtle text-info-emphasis">
                          {activeTeamPlayers.length} player(s) added
                        </span>
                      </div>
                      <div className="row g-2 align-items-end">
                        <div className="col-12 col-md-8">
                          <label htmlFor={`team-player-${activeTeamIndex}`} className="form-label fw-semibold">
                            Player from tournament roster
                          </label>
                          <div className="input-group mb-2">
                            <span className="input-group-text">Search</span>
                            <input
                              id={`team-player-${activeTeamIndex}`}
                              type="search"
                              className="form-control"
                              placeholder="Search by name, email, or role"
                              disabled={
                                isLoadingPlayers || !selectedTournamentId || mappedPlayers.length === 0 || remainingSlots === 0
                              }
                              value={playerSearchTerms[activeTeamIndex] || ''}
                              onChange={(event) =>
                                setPlayerSearchTerms((previous) =>
                                  previous.map((term, termIndex) =>
                                    termIndex === activeTeamIndex ? event.target.value : term,
                                  ),
                                )
                              }
                            />
                          </div>
                          <div className="border rounded bg-white" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                            {isLoadingPlayers ? (
                              <p className="text-muted mb-0 p-3">Loading players...</p>
                            ) : availablePlayers.length ? (
                              <ul className="list-unstyled mb-0">
                                {availablePlayers.map((player) => {
                                  const initial = player.name?.charAt(0)?.toUpperCase?.() || '?';
                                  const isChecked = pendingPlayers.includes(player.id);

                                  return (
                                    <li key={`player-${player.id || player._id}`} className="d-flex align-items-center gap-3 px-3 py-2 border-bottom">
                                      <div className="form-check m-0">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={`select-player-${activeTeamIndex}-${player.id}`}
                                          checked={isChecked}
                                          disabled={remainingSlots === 0}
                                          onChange={(event) =>
                                            togglePlayerSelection(activeTeamIndex, player.id, event.target.checked)
                                          }
                                        />
                                      </div>
                                      <div className="flex-shrink-0">
                                        {player.image ? (
                                          <img
                                            src={player.image}
                                            alt={player.name}
                                            className="rounded-circle"
                                            style={{ width: '44px', height: '44px', objectFit: 'cover' }}
                                          />
                                        ) : (
                                          <div
                                            className="rounded-circle bg-secondary-subtle text-secondary-emphasis d-flex align-items-center justify-content-center"
                                            style={{ width: '44px', height: '44px' }}
                                            aria-hidden="true"
                                          >
                                            <span className="fw-semibold">{initial}</span>
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex-grow-1">
                                        <p className="fw-semibold mb-0">{player.name}</p>
                                        {player.email && <p className="text-muted small mb-0">{player.email}</p>}
                                        {player.role && (
                                          <span className="badge bg-secondary-subtle text-secondary-emphasis mt-1">
                                            {player.role}
                                          </span>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <p className="text-muted mb-0 p-3">
                                No available players match your search. Players already assigned to a team are hidden until
                                they are removed.
                              </p>
                            )}
                          </div>
                          <small className="text-muted">
                            Select multiple players to add them in one step. Players already assigned to another team are
                            hidden from this list. Players are sourced from {selectedTournament?.name || 'the selected tournament'}{' '}
                            roster.
                          </small>
                          {playerLoadError && <p className="text-danger small mb-0 mt-1">{playerLoadError}</p>}
                        </div>
                        <div className="col-12 col-md-4 d-flex align-items-end">
                          <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => handleAddPlayer(activeTeamIndex)}
                            disabled={remainingSlots === 0 || isBusy}
                          >
                            {remainingSlots === 0
                              ? 'Player limit reached'
                              : `Add player${pendingPlayers.length > 1 ? 's' : ''} to Team ${activeTeamIndex + 1}`}
                          </button>
                        </div>
                      </div>

                      {activeTeamPlayers.length > 0 ? (
                        <div className="row g-2">
                          {activeTeamPlayers.map((player, playerIndex) => {
                            const playerId = getPlayerId(player);
                            const playerName = typeof player === 'string' ? playerLookup.get(player)?.name || player : player.name;
                            const role = typeof player === 'string' ? playerLookup.get(player)?.role || '' : player.role;
                            const email = typeof player === 'string' ? playerLookup.get(player)?.email || '' : player.email;
                            const image = typeof player === 'string' ? playerLookup.get(player)?.image || '' : player.image;
                            const initial = playerName?.charAt(0)?.toUpperCase?.() || '?';

                            return (
                              <div className="col-12 col-md-6 col-lg-4" key={`player-${playerId || `fallback-${playerIndex}`}`}>
                                <div className="d-flex align-items-center border rounded p-2 bg-white shadow-sm gap-2 h-100">
                                  <div className="flex-shrink-0">
                                    {image ? (
                                      <img
                                        src={image}
                                        alt={playerName}
                                        className="rounded-circle"
                                        style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <div
                                        className="rounded-circle bg-secondary-subtle text-secondary-emphasis d-flex align-items-center justify-content-center"
                                        style={{ width: '48px', height: '48px' }}
                                        aria-hidden="true"
                                      >
                                        <span className="fw-bold">{initial}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-grow-1">
                                    <p className="fw-semibold mb-0">{playerName}</p>
                                    {email && <p className="text-muted mb-0 small">{email}</p>}
                                    {role && <p className="text-muted mb-0 small">{role}</p>}
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleRemovePlayer(activeTeamIndex, playerIndex)}
                                    aria-label={`Remove ${playerName}`}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No players added yet. Start building the lineup.</p>
                      )}

                      <div className="row g-3">
                        <div className="col-12 col-md-4">
                          <label htmlFor={`team-captain-${activeTeamIndex}`} className="form-label fw-semibold">
                            Captain
                          </label>
                          <select
                            id={`team-captain-${activeTeamIndex}`}
                            className="form-select"
                            value={activeTeam.captain}
                            onChange={(event) => updateTeamField(activeTeamIndex, 'captain', event.target.value)}
                            disabled={!teamPlayerOptions.length}
                          >
                            <option value="">Select captain</option>
                            {teamPlayerOptions.map((player) => (
                              <option key={player.id || player._id} value={player.id}>
                                {player.label}
                              </option>
                            ))}
                          </select>
                          <small className="text-muted">Choose a leader from the players added to this team.</small>
                        </div>
                        <div className="col-12 col-md-4">
                          <label htmlFor={`team-vice-captain-${activeTeamIndex}`} className="form-label fw-semibold">
                            Vice captain
                          </label>
                          <select
                            id={`team-vice-captain-${activeTeamIndex}`}
                            className="form-select"
                            value={activeTeam.viceCaptain}
                            onChange={(event) => updateTeamField(activeTeamIndex, 'viceCaptain', event.target.value)}
                            disabled={!teamPlayerOptions.length}
                          >
                            <option value="">Select vice captain</option>
                            {teamPlayerOptions.map((player) => (
                              <option key={player.id || player._id} value={player.id}>
                                {player.label}
                              </option>
                            ))}
                          </select>
                          <small className="text-muted">Only players on this roster can be vice captain.</small>
                        </div>
                        <div className="col-12 col-md-4">
                          <label htmlFor={`team-wicket-${activeTeamIndex}`} className="form-label fw-semibold">
                            Wicket keeper
                          </label>
                          <select
                            id={`team-wicket-${activeTeamIndex}`}
                            className="form-select"
                            value={activeTeam.wicketKeeper}
                            onChange={(event) => updateTeamField(activeTeamIndex, 'wicketKeeper', event.target.value)}
                            disabled={!teamPlayerOptions.length}
                          >
                            <option value="">Select wicket keeper</option>
                            {teamPlayerOptions.map((player) => (
                              <option key={player.id || player._id} value={player.id}>
                                {player.label}
                              </option>
                            ))}
                          </select>
                          <small className="text-muted">Pick the wicket keeper from your listed players.</small>
                        </div>
                      </div>
                      <div className="alert alert-secondary mb-0" role="status">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary">{playersPerTeam}</span>
                            <div>
                              <p className="fw-semibold mb-0">Global players per team</p>
                              <p className="text-muted mb-0 small">Roster target applied automatically to this squad.</p>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <span className="badge bg-success-subtle text-success-emphasis">{activeTeamPlayers.length} listed</span>
                            <span className="badge bg-warning-subtle text-warning-emphasis">
                              {Math.max(playersPerTeam - activeTeamPlayers.length, 0)} open slots
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted mb-0">Add at least one team to start assigning players.</p>
              )}
            </div>
          </div>

          </div>
        </div>

      {logoEditor.open && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-75"
          style={{ zIndex: 1050 }}
        >
          <div className="card shadow-lg" style={{ maxWidth: '760px', width: '90%' }}>
            <div className="card-body d-grid gap-3">
              <div className="d-flex justify-content-between align-items-start gap-3">
                <div>
                  <p className="fw-semibold mb-1">Adjust team logo</p>
                  <p className="text-muted mb-0">Zoom and reposition to crop a clean, square logo before uploading.</p>
                </div>
                <button type="button" className="btn-close" aria-label="Close logo editor" onClick={resetLogoEditor} />
              </div>

              <div className="row g-3 align-items-center">
                <div className="col-12 col-md-6">
                  <div className="ratio ratio-1x1 border rounded position-relative overflow-hidden bg-light">
                    <img
                      src={logoEditor.imageSrc}
                      alt="Logo preview"
                      className="position-absolute top-50 start-50"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: `translate(-50%, -50%) translate(${logoEditor.offsetX}px, ${logoEditor.offsetY}px) scale(${logoEditor.zoom})`,
                      }}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 d-grid gap-3">
                  <div>
                    <label htmlFor="logo-zoom" className="form-label fw-semibold">
                      Zoom
                    </label>
                    <input
                      id="logo-zoom"
                      type="range"
                      min="0.5"
                      max="2.5"
                      step="0.1"
                      value={logoEditor.zoom}
                      className="form-range"
                      onChange={(event) =>
                        setLogoEditor((previous) => ({ ...previous, zoom: Number(event.target.value) }))
                      }
                    />
                    <p className="text-muted small mb-0">Zoom in to fill the frame or zoom out to fit the full emblem.</p>
                  </div>
                  <div className="row g-2">
                    <div className="col-12 col-sm-6">
                      <label htmlFor="logo-offset-x" className="form-label fw-semibold">
                        Horizontal adjust
                      </label>
                      <input
                        id="logo-offset-x"
                        type="range"
                        min="-180"
                        max="180"
                        step="2"
                        value={logoEditor.offsetX}
                        className="form-range"
                        onChange={(event) =>
                          setLogoEditor((previous) => ({ ...previous, offsetX: Number(event.target.value) }))
                        }
                      />
                    </div>
                    <div className="col-12 col-sm-6">
                      <label htmlFor="logo-offset-y" className="form-label fw-semibold">
                        Vertical adjust
                      </label>
                      <input
                        id="logo-offset-y"
                        type="range"
                        min="-180"
                        max="180"
                        step="2"
                        value={logoEditor.offsetY}
                        className="form-range"
                        onChange={(event) =>
                          setLogoEditor((previous) => ({ ...previous, offsetY: Number(event.target.value) }))
                        }
                      />
                    </div>
                  </div>
                  <p className="text-muted small mb-0">
                    The cropped image is optimized to a square JPEG for consistent sharing across the management tools.
                  </p>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 flex-wrap">
                <button type="button" className="btn btn-outline-secondary" onClick={resetLogoEditor}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleApplyLogo}>
                  Apply crop & optimize
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionTeamSetupPage;
